import { Injectable } from '@angular/core';
import { Item } from '../models/cart-data.model';
declare let gtag: Function;
declare let dataLayerPush: Function;

// TODO: declare all types missing
@Injectable({
  providedIn: 'root'
})
export class GtagService {
  constructor() { }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/setting-values */
  public set(params: any): void {
    return gtag('set', params);
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/events */
  public event(action: string, params?: any): Promise<void> {
    // Wraps the event call into a Promise
    return new Promise((resolve, reject) => {
      try {
        // Triggers a 3s time-out timer
        const tmr = setTimeout(() => reject(new Error('gtag call timed-out')), 3000);
        // Performs the event call resolving with the event callback
        gtag('event', action, {
          ...params,
          event_callback: () => {
            clearTimeout(tmr);
            resolve();
          }
        });
      } catch (e) {
        // Rejects the promise on errors
        reject(e);
      }
    });
  }

  public gtm(action: string, params?: any) {
    dataLayerPush({
      event: action,
      ecommerce: {
        ...params
      }
    });
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/pages */
  public pageView(page_title?: string, page_path?: string, page_location?: string) {
    return this.event('page_view', { page_title, page_location, page_path });
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/exceptions */
  public exception(description?: string, fatal?: boolean) {
    return this.event('exception', { description, fatal });
  }

  public login(method: string) {
    return this.event('login', { method });
  }

  public signUp(method?: string) {
    return this.event('sign_up', { method });
  }

  public search(search_term: string) {
    return this.event('search', { search_term });
  }

  public viewCart(items?: Item[]) {
    // exclude promotions from cart | temporary implementation until we implement proper promotions with GA4
    items = items.filter((x) => !x.IsPromotion);
    this.gtm('view_cart', {
      value: items.reduce((sum, c) => (sum += c.Quantity * c.UnitPrice), 0),
      items: items.map((x) => this.toGTagItem(x))
    });
  }

  public removeFromCart(item?: Item) {
    this.gtm('remove_from_cart', {
      value: item.UnitPrice * item.Quantity,
      items: [this.toGTagItem(item)]
    });
  }

  public removeItemsFromCart(items?: Item[]) {
    if (items.length === 0) return;
    this.gtm('remove_from_cart', {
      value: items.reduce((sum, item) => (sum += item.UnitPrice * item.Quantity), 0),
      items: items.map((x) => this.toGTagItem(x))
    });
  }

  public addToWishlist(item?: Item) {
    this.gtm('add_to_wishlist', {
      value: item.UnitPrice * item.Quantity,
      items: [this.toGTagItem(item)]
    });
  }

  public addItemsToCart(addedItems: Item[], source: string = '') {
    if (addedItems.length === 0) {
      return;
    }

    const value = addedItems.reduce((sum, item) => (sum += item.UnitPrice * item.Quantity), 0);
    const items = addedItems.map((x) => this.toGTagItem(x));

    let component = '';

    if (source && source.includes('-')) {
      const split = source.split('-');
      source = split[0].trim();
      component = split[1].trim();

      this.gtm('add_to_cart', { value: value, items, custom_source: source, component });
    }
    else {
      this.gtm('add_to_cart', { value, items });
    }
  }

  private toGTagItem(item: Item): GTagItem {
    const gtagItem = {
      item_id: item.Webcode,
      item_name: item.Description,
      item_brand: item.Manufacturer,
      item_category: item.CategoryLevel1,
      item_category2: item.CategoryLevel2,
      item_category3: item.CategoryLevel3,
      item_category4: item.CategoryLevel4,
      price: item.UnitPrice,
      quantity: item.Quantity
    } as GTagItem;
    const props = Object.getOwnPropertyNames(gtagItem);
    props.forEach((x) => {
      if (gtagItem[x] === null || gtagItem[x] === undefined) {
        delete gtagItem[x];
      }
    });
    return gtagItem;
  }
}

export interface GTagItem {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  price: number;
  quantity: number;
}
